import React, { useEffect, useState } from 'react';
import EditClientBillingInfoFields, {
    FormValue,
    formValueToCreateClientRecordInvitationRequest,
    getDefaultFormValue,
} from './EditClientBillingInfoFields';
import UseOrganizationClientRecordBillingDetails from '../../../hooks/api/useOrganizationClientRecordBillingDetails';
import { Form } from '../../v2BaseComponents/Form';
import { getIntl } from '../../../utils/intl';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import Stack from '../../v2BaseComponents/Stack';
import { AlertIcon } from '../../v2BaseComponents/Icon';
import Text from '../../v2BaseComponents/Text';

const intl = getIntl('profiles.editClientBillingInfoGate', {
    update: 'Update',
    clientMissingBillingInformation: 'Client is missing billing information',
    subHeader: 'In order to invoice this client, you must provide billing information.',
});

export type RequiredClientBillingInfoGateOwnProps = {
    clientRecordId: string;
    children: () => React.ReactNode;
};

export type RequiredClientBillingInfoGateProps = RequiredClientBillingInfoGateOwnProps;

const RequiredClientBillingInfoGate: React.FC<RequiredClientBillingInfoGateProps> = ({ clientRecordId, children }) => {
    const [initialFormValue, setInitialFormValue] = useState<FormValue | null>(null);
    const {
        isLoadingBillingDetails,
        isUpdatingBillingDetails,
        updateBillingDetails,
        billingDetails,
        isFetchingBillingDetails,
    } = UseOrganizationClientRecordBillingDetails({
        organizationClientRecordId: clientRecordId,
    });
    useEffect(() => {
        if (!billingDetails) return;

        const defaultValue = getDefaultFormValue(billingDetails);
        setInitialFormValue(defaultValue);
    }, [billingDetails]);

    const renderContent = () => {
        if (!billingDetails) return null;

        if (billingDetails.status === 'insufficient-information') {
            return (
                <Form
                    fillContainer
                    actionContainerSubmit
                    initialValues={initialFormValue!}
                    onSubmit={(values) => {
                        const request = formValueToCreateClientRecordInvitationRequest(values);
                        updateBillingDetails(request);
                    }}
                    saveText={intl('update')}
                    saveIcon="CheckCircle"
                    forceStackFields
                >
                    {() => (
                        <Stack vertical align="center" space={2}>
                            <Stack vertical align="center" space={6}>
                                <AlertIcon size="lg" variant="warning" name="AlertTriangle" />
                                <Text variant="body-1" alignText="center">
                                    {intl('clientMissingBillingInformation')}
                                </Text>
                                <Text variant="caption-2" color="Gray-Text" alignText="center">
                                    {intl('subHeader')}
                                </Text>
                            </Stack>
                            <EditClientBillingInfoFields />
                        </Stack>
                    )}
                </Form>
            );
        } else {
            return children();
        }
    };

    return (
        <AnimateLoadedContent
            isLoading={isLoadingBillingDetails || isUpdatingBillingDetails || isFetchingBillingDetails}
        >
            {renderContent}
        </AnimateLoadedContent>
    );
};

export default React.memo(RequiredClientBillingInfoGate);
